import { Link } from "react-router";
import { usePageMeta } from "../components/Layout.tsx";

export function HowItWorks() {
  usePageMeta(
    "How it works",
    "The scheduling, grading and scoring rules behind Coding, written out in full.",
  );

  return (
    <article className="prose-page">
      <h1>How it works</h1>
      <p className="lede">
        The rules are written out in full because a practice tool that hides its scoring is asking you
        to trust a number you cannot check.
      </p>

      <h2>It schedules patterns, not problems</h2>
      <p>
        Each problem belongs to a pattern: topological sort, sliding window, monotonic stack. Scheduling
        per problem degrades into memorising one specific solution, which feels like progress and
        transfers to nothing. Once a pattern holds several problems, coming back to it hands you one you
        have not seen recently, so you have to rederive the shape rather than recall the answer.
      </p>

      <h2>The tests change between reps</h2>
      <p>
        Some cases build their inputs from a seed rather than using fixed ones, and the seed is how many
        times you have attempted that problem. A drill you saw last week hands you a different graph,
        a different stream, a different index. Those cases are checked either against the rules the
        problem states, where more than one answer is correct, or against the same answer worked out
        the slow obvious way, so passing them means the solution is right rather than remembered.
      </p>
      <p>
        Within one attempt the inputs are fixed. Pressing Run twice cannot hand you a different problem,
        and a case that passed a minute ago does not fail on an identical resubmit.
      </p>

      <h2>Grading is about the clock, not the keyboard</h2>
      <table>
        <thead>
          <tr>
            <th>Grade</th>
            <th>When</th>
            <th>What happens next</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Failed</td>
            <td>No passing solution, or you revealed the answer</td>
            <td>Resets. Back in 20 minutes.</td>
          </tr>
          <tr>
            <td>Hard</td>
            <td>Passed, but over the time budget</td>
            <td>Interval grows barely, 1.2&times;</td>
          </tr>
          <tr>
            <td>Good</td>
            <td>Passed inside the budget</td>
            <td>Interval grows by your ease, about 2.5&times;</td>
          </tr>
          <tr>
            <td>Easy</td>
            <td>Passed first run, in under 40% of the budget</td>
            <td>Interval grows fastest, and ease goes up</td>
          </tr>
        </tbody>
      </table>
      <p>
        Running the tests many times never drags you down to Hard on its own. Iterating quickly is a good
        habit in a real screen, and penalising it would train you to sit and stare at the code instead.
        The run count costs you points, not a grade.
      </p>

      <h2>Revealing the solution is a failure</h2>
      <p>
        Even if the tests then pass. The point is production from memory, and a card you had to be told
        is not one you know. It scores zero and resets the interval. There is no penalty for taking that
        option, and no reward for pretending you did not need it.
      </p>

      <h2>There is no daily limit</h2>
      <p>
        Do one, do twenty. You get what is due first, then anything you have never seen, then everything
        else pulled forward. Those pulled-forward reps are marked as early and grow their interval by
        half as much, because recalling something you saw an hour ago proves far less about retention
        than recalling it a week later. Without that, one heavy session would push every problem months
        out on the strength of very easy repetitions.
      </p>

      <h2>Scoring</h2>
      <p>
        A pass scores its base points multiplied by how much of the budget you had left, reduced for each
        extra run, and nudged up by your daily streak. A fail or a reveal scores zero, so the total only
        ever counts problems you solved unaided.
      </p>

      <h2>Paste is blocked</h2>
      <p>
        In the editor, by intercepting the paste event, drag-and-drop and middle-click. Anyone determined
        can get around it with devtools. That is not the threat being defended against. It exists so that
        the easy, thoughtless path is typing it out.
      </p>

      <h2>The leaderboard is opt-in</h2>
      <p>
        You are on nobody's list by default. Appearing on the <Link to="/scoreboard">scoreboard</Link>{" "}
        requires choosing a handle and turning it on yourself, and it publishes that handle and your
        scores, never your name or your email. Turning it off, or clearing the handle, removes you
        immediately.
      </p>

      <h2>Your code stays in your browser</h2>
      <p>
        Solutions are compiled and run client-side, in a worker that is destroyed after every run. What
        you write is never sent to the server. The only things stored are the outcome of an attempt: how
        long it took, how many runs, whether it passed.
      </p>
    </article>
  );
}
